/** PM Thread View — open/close threads, header updates. */

var pmThreadLoadSeq = 0;
var activePmThreadLoad = null;
// Set true once the page begins unloading (navigation, reload, tab close), so an
// in-flight thread load torn down by that unload is recorded as a cancellation
// rather than a failure. pagehide is bfcache-safe (beforeunload is not).
var pmPageUnloading = false;

// Fraction of pm-slow-load:5s diagnostics that is reported to Sentry. The
// "thread load exceeded 5s" signal only needs a sampled trend, and the
// unsampled volume dominates the web Sentry project. Kept events carry the
// pm_slow_load_sampled tag so the trend stays identifiable (true volume is the
// kept count divided by this rate). Held at 0.2 while PM load latency is under
// active investigation so per-event context is retained for root-causing. Only
// this info-level latency hint is sampled. The warning-level pm-load-failed
// below reports every occurrence.
var PM_SLOW_LOAD_SAMPLE_RATE = 0.2;
// Resource timing records resolved URLs even when ajax receives a relative
// path. Resolution can throw in restricted browser environments, where the
// relative fallback preserves the failure path and reports no matching entry.
var PM_THREAD_LOAD_URL = (function() {
  try {
    return new URL('/app/messages/load', window.location.href).href;
  } catch (e) {
    return '/app/messages/load';
  }
})();

/**
 * Whether an ajax failure is a teardown of a page that is going away, rather
 * than a fault the user should see an error for.
 *
 * Only status 0 qualifies: anything that carries an HTTP status reached the
 * server and came back, so it is a real response however bad. Of the status-0
 * shapes, 'error' and '' are the connection-level teardowns.
 *
 * 'timeout' is not a cancellation at any visibility. This branch also skips the
 * error UI, and a hidden page is one the user is coming back to, unlike an
 * unloading one. Nothing else clears the spinner: finishPmThreadLoad only
 * resets module state, the pageshow handler requires a bfcache restore, and the
 * polling recovery path returns before the spinner is rebuilt. So a timeout
 * renders the error and is reported as a failure, and the tags carry what is
 * needed to count how many of those failures were backgrounded.
 *
 * The signals come from the same object the tags are built from, so the reading
 * that decided the branch is the reading the event reports. Sampling
 * visibilityState here as well would let the two disagree, and the disagreement
 * would be invisible: an event tagged page_hidden=0 that landed on the
 * cancellation branch looks exactly like a misclassification.
 *
 * @param {{httpStatus: number|string, pageHidden: boolean, pageUnloading: boolean}} signals
 * @param {string} textStatus jQuery's failure category
 * @returns {boolean}
 */
function pmIsCancelledLoadTeardown(signals, textStatus) {
  if (!signals.pageHidden && !signals.pageUnloading) return false;
  if (signals.httpStatus !== 0) return false;

  return textStatus === 'error' || textStatus === '';
}

// navigator.onLine is a weak signal on its own: it reports false only when the
// device has no interface at all, so a captive portal or a dead cellular leg
// still reads true. It is recorded because the false cases are unambiguous, not
// because true means the connection was healthy.
//
// The try is load-bearing rather than defensive habit. Both helpers are
// evaluated as arguments inside the ajax failure handler, ahead of the branch
// that renders the error, so a throw here leaves the spinner on screen with no
// error and no way back. safe-storage.js exists for the same reason: an
// anti-fingerprinting shim can install a throwing getter on a navigator
// property, and that must not escape into a path that is not about the network.
function pmNetworkOnline() {
  try {
    return typeof navigator.onLine === 'boolean' ? navigator.onLine : null;
  } catch (e) {
    return null;
  }
}

// effectiveType is absent on Safari, which is the majority of the population
// this is meant to explain, so null is an expected value here rather than a
// failure. It still separates Android and desktop Chrome, where a '2g'/'slow-2g'
// reading at the moment of a sub-second status-0 failure is the signal wanted.
function pmConnectionType() {
  try {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return c && c.effectiveType ? String(c.effectiveType) : null;
  } catch (e) {
    return null;
  }
}

// Client-side interception can prevent a request from producing a resource
// timing entry, while a connection failure after reaching the network layer
// produces an entry. The attempt timestamp excludes entries from earlier
// requests to the same URL.
//
// Old browsers may not implement resource timing, and a full timing buffer can
// also omit an entry. Absence is therefore used only alongside the retry
// outcome, and a throwing browser shim must not escape into failure handling.
function pmResourceTimingEntryForRequest(absoluteUrl, sinceEpochMs) {
  try {
    if (!window.performance || typeof performance.getEntriesByName !== 'function') return null;
    var entries = performance.getEntriesByName(absoluteUrl, 'resource');
    if (!entries.length || !performance.timeOrigin) return entries.length ? entries[entries.length - 1] : null;
    for (var i = entries.length - 1; i >= 0; i--) {
      if (performance.timeOrigin + entries[i].startTime >= sinceEpochMs) return entries[i];
    }
    return null;
  } catch (e) {
    return null;
  }
}

function pmNetworkSnapshot() {
  return { online: pmNetworkOnline(), type: pmConnectionType() };
}

function pmNetworkOnlineTag(online) {
  return online === null ? 'unknown' : (online ? '1' : '0');
}

// When the tab went to the background, bucketed so it can be grouped on.
// The raw millisecond value stays in extra for reading one event, but a
// per-millisecond tag would be unbounded cardinality and Sentry cannot
// threshold on extra at all, so the question "how many of these backgrounded
// early" needs the value in this shape to be answerable.
//
// The boundaries are the ones the split turns on: 'never' is a load the user
// watched throughout, 'under_1s' is a background so soon after the click that
// they never waited, and 'over_30s' cannot precede a 30s timeout, so it marks a
// tab backgrounded after the request had already failed. 'at_start' covers a
// page hidden before the request went out and one backgrounded inside the same
// millisecond, which hidden_at_start tells apart.
function pmHiddenAfterBucket(hiddenAfterMs) {
  if (hiddenAfterMs === null || hiddenAfterMs === undefined) return 'never';
  if (hiddenAfterMs <= 0) return 'at_start';
  if (hiddenAfterMs < 1000) return 'under_1s';
  if (hiddenAfterMs < 5000) return '1s_5s';
  if (hiddenAfterMs < 30000) return '5s_30s';

  return 'over_30s';
}

/**
 * The network/visibility discriminators, shaped as Sentry tags.
 *
 * Sentry indexes tags and not extra, so a value kept only in extra can be read
 * one event at a time but never counted, filtered or grouped. Splitting a
 * group by these values is the whole reason they are recorded, so they belong
 * here, the way text_status and pm_slow_load_sampled already do.
 *
 * pageHidden and pageUnloading are the pair pmIsCancelledLoadTeardown decides
 * on, so both are tagged. A same-tab navigation is the case pageUnloading
 * exists for: visibilityState still reads 'visible' there, so without it that
 * population carries the same tag values as a foreground network failure, and
 * the split this instrumentation is for cannot be made.
 *
 * The network reading carries its epoch in the name, and both epochs are
 * recorded, because neither answers the question alone. A reading taken when
 * the request was issued is 'online' for a genuine failure too, since the drop
 * happens afterwards. A reading taken at capture is 'online' for a suspended
 * iOS tab too, because the 30s timeout cannot fire until the page resumes and
 * connectivity is back by then, so it cannot contradict the hypothesis it was
 * added to test. The pair discriminates: at_start 1 with at_capture 0 is a
 * connection that genuinely went away.
 *
 * connection.effectiveType is absent on Safari and coarse on Chrome, where it
 * is a rounded estimate refreshed at intervals rather than an instantaneous
 * reading, so a '4g' value during a real drop is expected and does not
 * exonerate the network.
 *
 * Tag values must be strings, and a null is dropped rather than stored, which
 * would make "the API was unavailable" indistinguishable from "the field was
 * never set". Absent readings are therefore reported as the string 'unknown'.
 * Every value here is low cardinality: booleans and a five-value enum.
 * Identifiers stay in extra, where unbounded cardinality is harmless.
 *
 * Signals are named rather than positional. Four booleans in a row transpose
 * silently: the call still runs and still produces a plausible tag set.
 *
 * @param {{hiddenAtStart: boolean, hiddenDuring: boolean, pageHidden: boolean,
 *   pageUnloading: boolean, httpStatus: number|string, hiddenAfterMs: ?number,
 *   netAtStart: {online: ?boolean, type: ?string}}} signals
 */
function pmDiagnosticSignalTags(signals) {
  var atCapture = pmNetworkSnapshot();
  var atStart = signals.netAtStart || { online: null, type: null };

  return {
    hidden_at_start: signals.hiddenAtStart ? '1' : '0',
    hidden_during: signals.hiddenDuring ? '1' : '0',
    hidden_after: pmHiddenAfterBucket(signals.hiddenAfterMs),
    page_hidden: signals.pageHidden ? '1' : '0',
    page_unloading: signals.pageUnloading ? '1' : '0',
    net_online_at_start: pmNetworkOnlineTag(atStart.online),
    net_online_at_capture: pmNetworkOnlineTag(atCapture.online),
    net_type_at_start: atStart.type === null ? 'unknown' : atStart.type,
    net_type_at_capture: atCapture.type === null ? 'unknown' : atCapture.type,
    http_status: String(signals.httpStatus)
  };
}

/**
 * The layout geometry threads_main_resize() last computed, shaped as Sentry tags.
 *
 * A pane that blanks or clips leaves the request untouched, so nothing else on
 * these events separates it from a healthy load. available_height is the pin
 * written onto .privateMessages and container_top is the offset it was measured
 * from, so a zero or near-zero height with a plausible container_top is the
 * signature of a pin taken before the leaderboard filled.
 *
 * Pixel values are tags rather than extra because the question is how many
 * events carry a collapsed pane, and Sentry cannot count or threshold on extra.
 * They are bounded by real viewport sizes rather than by an identifier space.
 *
 * The whole set reports 'unknown' before the first layout pass, so a default
 * cannot be read as a measurement.
 */
function pmLayoutTags() {
  var layout = window.pmLastLayout;
  var viewportWidth = String(Math.round(window.innerWidth || 0));

  if (!layout || layout.availableHeight === null) {
    return {
      available_height: 'unknown',
      container_top: 'unknown',
      has_no_threads: 'unknown',
      thread_open: 'unknown',
      viewport_w: viewportWidth
    };
  }

  return {
    available_height: String(Math.round(layout.availableHeight)),
    container_top: String(Math.round(layout.containerTop)),
    has_no_threads: layout.hasNoThreads ? '1' : '0',
    thread_open: layout.threadOpen ? '1' : '0',
    viewport_w: viewportWidth
  };
}

/**
 * Watches visibility for the life of one thread load.
 *
 * The readings at the two endpoints of a request cannot say whether the tab
 * was backgrounded while it ran. A tab frozen for the whole 30s and a genuine
 * hang the user walked away from both end hidden, and every caller that
 * reaches the request is a foreground gesture, so the start reading is very
 * nearly always visible. The transition is what separates them.
 *
 * release() is idempotent and is called on the done, fail and always seams.
 * jQuery 1.10.2 fires callbacks with no try/catch, so a throw in a done
 * handler skips every callback registered after it. A listener released only
 * in always() would outlive the request and keep this closure, and its DOM,
 * reachable for the life of the page.
 *
 * One watcher spans both attempts of a retried load, so the seam that hands off
 * to a retry is the one place that does not release. The attempt that finalizes
 * the load releases it.
 */
function pmCreateLoadVisibilityWatcher() {
  var startedAt = Date.now();
  var hiddenAtStart = document.visibilityState === 'hidden';
  var watcher = {
    hiddenAtStart: hiddenAtStart,
    hiddenDuring: hiddenAtStart,
    // hiddenDuring is a latch with no duration, so on a 30s timeout a single
    // notification banner sets it while a 250ms failure has no chance to. Left
    // alone it would track request duration rather than cause, in exactly the
    // bucket that needs discriminating. This says when it first happened.
    hiddenAfterMs: hiddenAtStart ? 0 : null
  };
  var onVisibility = function() {
    if (document.visibilityState === 'hidden') {
      if (watcher.hiddenAfterMs === null) {
        watcher.hiddenAfterMs = Date.now() - startedAt;
      }
      watcher.hiddenDuring = true;
    }
  };

  document.addEventListener('visibilitychange', onVisibility);
  watcher.release = function() {
    document.removeEventListener('visibilitychange', onVisibility);
  };

  return watcher;
}

function capturePmThreadDiagnostic(message, level, tags, extra) {
  if (!window.Sentry) return;
  Sentry.captureMessage(message, {
    level: level,
    tags: tags || {},
    extra: extra || {}
  });
}

function isActivePmThreadLoad(loadState) {
  return activePmThreadLoad && activePmThreadLoad.id === loadState.id;
}

function getActivePmThreadId() {
  return window.get_active_thread_id ? window.get_active_thread_id() : null;
}

function clearPmThreadLoadTimeout(loadState) {
  if (!loadState || !loadState.timeoutId) return;
  clearTimeout(loadState.timeoutId);
  loadState.timeoutId = null;
}

function cancelActivePmThreadLoad() {
  if (!activePmThreadLoad) return null;

  var loadState = activePmThreadLoad;
  clearPmThreadLoadTimeout(loadState);

  if (loadState.jqXHR && loadState.jqXHR.readyState !== 4) {
    loadState.jqXHR.abort();
  }

  return loadState;
}

function finishPmThreadLoad(loadState) {
  if (!isActivePmThreadLoad(loadState)) return;

  clearPmThreadLoadTimeout(loadState);
  activePmThreadLoad = null;
  window.pm_ajax_request_active = 0;
}

// Diagnostic: detect bfcache restoration on the messages page
window.addEventListener('pageshow', function(e) {
  // A bfcache restore preserves module state, so clear the unload flag that
  // pagehide set on the way out. Otherwise a restored page keeps
  // pmPageUnloading === true and would misclassify a genuine foreground load
  // failure as a cancellation, suppressing the error UI.
  pmPageUnloading = false;
  if (!e.persisted || !document.querySelector('.privateMessages')) return;
  var spinner = document.getElementById('pm-loading-spinner');
  console.warn('[pm-diag] pageshow from bfcache, spinner=' + !!spinner);
  if (spinner) {
    capturePmThreadDiagnostic('pm-stuck-spinner:bfcache', 'info', {
      source: 'bfcache',
      state: 'bfcache_recovery'
    }, {
      active_thread_id: getActivePmThreadId(),
      active_request_id: activePmThreadLoad ? activePmThreadLoad.id : null,
      active_request_thread_id: activePmThreadLoad ? activePmThreadLoad.threadId : null
    });
    var activeThread = document.querySelector('#threads_left .pm_thread.active');
    if (activeThread) {
      window.view_thread(activeThread.id.replace('thread_', ''), true);
    }
  }
});

window.addEventListener('pagehide', function() {
  pmPageUnloading = true;
});

window.open_active_thread_by_link = function(activeThread) {
  // With column-reverse, scrollTop=0 is the bottom
  let trt = $("#threads_right");
  window.setTimeout(function() {
    if (typeof trt.scrollTo === "function" && trt.length) {
      trt.scrollTo({
        top: 0,
        // Prevent horizontal scrolling
        left: 0,
        behavior: "smooth"
      });
    }
  }, 200);

  window.update_user_header(activeThread.data("thread-id"));

  // Show the conversation header. Keep the compose box hidden for inactive
  // contacts (deleted / paused / pending review) — they can't receive messages.
  // This direct-link path has no AJAX load to re-hide it, so gate it here.
  $("#conversation-header").removeClass("hidden");
  if (activeThread.data("inactive")) {
    $("#threads_right_bottom").addClass("hidden");
  } else {
    $("#threads_right_bottom").removeClass("hidden");
  }

  // Open side panel for the active thread's user. The backend decides whether to
  // log a profile view based on whether a thread with messages already exists.
  if (window.SidePanel) {
    var uid = activeThread.data('uid');
    if (uid) {
      window.SidePanel.open(uid, { clickSource: 2, context: 'messages' });
    }
  }
};

// Mirrors the `thread.new` conditionals in pm/macros.twig — must be kept in sync
// with the utilities the template bakes in for the unread state.
window.mark_thread_row_read = function(thread) {
  if (!thread || !thread.length) return;
  thread.removeClass("new");
  thread.find(".pm-unread-badge, .pm_new_circle").remove();
  thread.find("span.truncate.font-bold")
    .removeClass("font-bold").addClass("font-medium");
  thread.find("span.text-sky-400")
    .removeClass("text-sky-400").addClass("text-gray-300");
  thread.find("a.th_snippet.text-gray-800")
    .removeClass("text-gray-800 font-medium").addClass("text-gray-500");
};

window.remove_msg_highlighting = function() {
  if (window.messages_highlighted === 0) return;
  if ($("#single_thread_wrapper").length > 0) {
    $("#conversation").find(".pm_new").removeClass("pm_new");
  } else {
    $("#threads_right").find(".pm_new").removeClass("pm_new");
  }
  window.messages_highlighted = 0;
};

window.get_newest_msg_id_in_thread = function() {
  let lm;
  if ($("#single_thread_wrapper").length > 0) {
    // Classic view
    lm = $("#conversation").find(".pm_msg:not(.optimistic-msg):first");
  } else {
    // Split view — skip optimistic messages that haven't been confirmed by server
    lm = $("#threads_right").find(".pm_msg:not(.optimistic-msg):last");
  }
  if (lm.length) {
    return lm.attr("id").replace("msg_", "");
  }
  return false;
};

window.update_change_view_link = function(thread_id) {
  // Update "change_view" link
  var view = window.pm_view_mode === "chat" ? "paged" : "chat";
  if (typeof thread_id == "undefined") {
    $("#change_view").attr("href", "/app/messages?view=" + view);
  } else {
    $("#change_view").attr("href", "/app/messages?view=" + view + "&thread_id=" + thread_id);
  }
};

window.update_user_header = function(thread_id) {
  const thread = $("#thread_" + thread_id);
  $("#current_thread_avatar").attr("src", thread.data("thumb"));
  $("#current_thread_username").text(thread.data("name"));
  $("#current_thread_link").attr("href", thread.data("profile-link"));

  // Let the call affordance track the open conversation's contact.
  document.dispatchEvent(new CustomEvent("pm:thread-opened", {
    detail: {
      uid: String(thread.data("uid") || ""),
      username: thread.data("username") || "",
      name: thread.data("name") || "",
      avatar: thread.data("thumb") || "",
    }
  }));

  if (thread.data("inactive")) {
    $("#current_thread_status").text("");
    return;
  }

  $("#current_thread_status").text(thread.data("contact-status") || "");
};

window.update_header_status = function(statusText) {
  $("#current_thread_status").text(statusText || "");
};

window.view_thread = function(thread_id, reload_messages) {
  // Hide the Message Requests action bar by default (#3039); openRequestPeek
  // re-shows it for request conversations after this runs. Also clear the
  // request-readonly state (react/reply gating) so normal threads are editable;
  // showRequestBar re-applies it for request peeks.
  var _reqBar = document.getElementById('message-request-bar');
  if (_reqBar) _reqBar.classList.add('hidden');
  var _tr = document.getElementById('threads_right');
  if (_tr) _tr.classList.remove('pm-request-readonly');

  // Clear reply state when switching to a different thread
  var currentThread = window.get_active_thread_id ? window.get_active_thread_id() : null;
  if (window.cancelReply && (reload_messages || String(thread_id) !== String(currentThread))) window.cancelReply();

  $("#alert_bar").remove();

  var msg = $("#message"),
    tl = $("#threads_left"),
    thread = $("#thread_" + thread_id),
    tm = $("#threads_main"),
    use_browser_cached_draft = typeof window.last_draft[thread_id] != "undefined",
    localDraft = window.draftStorage.load(thread_id);

  // Preserve thread list scroll — guard against reflow-induced resets
  var tlEl = tl[0];
  var _savedScroll = tlEl ? tlEl.scrollTop : 0;
  function _keepScroll() { if (tlEl) tlEl.scrollTop = _savedScroll; }
  // Run a short-lived interval to continuously restore scroll position
  // across all async reflows (style injection, AJAX DOM updates, etc.)
  if (tlEl && _savedScroll > 0) {
    var _guardCount = 0;
    var _scrollGuard = setInterval(function() {
      tlEl.scrollTop = _savedScroll;
      if (++_guardCount >= 30) clearInterval(_scrollGuard);
    }, 10);
    // Also clear guard if user scrolls manually
    var _clearGuard = function() {
      clearInterval(_scrollGuard);
      tlEl.removeEventListener('wheel', _clearGuard);
      tlEl.removeEventListener('touchmove', _clearGuard);
    };
    tlEl.addEventListener('wheel', _clearGuard, { once: true });
    tlEl.addEventListener('touchmove', _clearGuard, { once: true });
  }

  // If reload_messages is false, we only mark thread as active. Without reset form.

  if (reload_messages) {
    // Make 'send message' smaller and make 'message' textarea empty
    $("#threads_main").removeClass("send");
    msg.val("");
    window.rebaseMessageComposition?.('', 'empty');
    $("#send_thread_id").val(thread_id);
    // Remove autosaved msg
    $("#markitup_msg").empty();
  }

  // Remove "new" in 3 seconds
  window.setTimeout(function() {
    window.remove_new_mark();
  }, 3000);

  // Restore draft from local storage, in-memory cache, or server (via AJAX below)
  if (reload_messages && (localDraft !== null || use_browser_cached_draft)) {
    var draft = localDraft !== null ? localDraft : window.last_draft[thread_id];
    msg.val(draft);
    window.rebaseMessageComposition?.(draft, 'restored_draft');
    if (draft) {
      $("#threads_main").addClass("send");
    }
    if (window.updateSendButtonState) window.updateSendButtonState();
  }
  // Remove empty-state placeholder before showing the pane to prevent flash on mobile
  $("#pm-empty-state").remove();

  // With this class '#threads_right' and 'send message' form will be visible
  tm.addClass("threads--open");
  _keepScroll();
  tm.removeClass("pm-placeholder-hidden");
  $("#threads_left_container")[0].style.removeProperty("width");
  $("#threads-right__container").removeClass("hidden").addClass("flex").show();
  $("#separator").show();
  $("#pm-show-placeholder-btn").addClass("hidden").removeClass("flex");
  $("#pm-dismiss-placeholder-btn").addClass("hidden");
  _keepScroll();

  // Show the conversation header. Keep the compose box hidden for inactive
  // contacts (deleted / paused / pending review) — they can't receive messages.
  $("#conversation-header").removeClass("hidden");
  if (thread.data("inactive")) {
    $("#threads_right_bottom").addClass("hidden");
  } else {
    $("#threads_right_bottom").removeClass("hidden");
  }
  _keepScroll();

  // Hide jump-to-latest when switching threads (scroll handler will show it if needed)
  $("#jump-to-latest-btn").addClass("hidden").removeClass("flex");

  // Hide any open dropdown menus
  $("#conversation-actions-menu").addClass("hidden");
  if (window.SwipeActions) window.SwipeActions.closeAll();

  // Update responsive layout
  window.threads_main_resize();
  _keepScroll();

  window.update_user_header(thread_id);

  // Remove all old active threads
  tl.find(".pm_thread").removeClass("active");
  // Set new thread as active
  thread.addClass("active");
  _keepScroll();

  // Scroll page to top (use native scrollTo to avoid affecting inner scroll containers)
  if ($(window).height() > 590) {
    window.scrollTo(0, 0);
  }
  _keepScroll();

  window.update_change_view_link(thread_id);

  // If reload_messages is false we only mark thread as active. Without reloading messages.
  if (!reload_messages) return false;

  // Clear unread-state styling baked into the thread row's template
  window.mark_thread_row_read(thread);

  // Show 'hide thread' button. Remove css class which rendered on server side if any
  var ctw = $("#close_thread_wrap");
  ctw.removeClass("visible");
  ctw.show();

  var c = $("#threads_right");

  // Show a loading spinner while messages load
  var stillLoadingLabel = (window.t ? window.t('common.pm.still_loading') : 'Still loading…');
  c.html(
    '<div id="pm-loading-spinner" class="flex flex-col items-center justify-center h-full flex-1 gap-3">' +
      '<div class="w-6 h-6 border-2 border-gray-300 border-t-sky-500 rounded-full animate-spin"></div>' +
      '<div id="pm-loading-still" class="hidden text-sm text-gray-500 dark:text-gray-400">' + $('<span>').text(stillLoadingLabel).html() + '</div>' +
    '</div>'
  );

  // Pre-position container to bottom before loading to prevent flash
  window.prePositionToBottom(c);

  cancelActivePmThreadLoad();

  var _loadStart = Date.now();
  var _loadVisibility = pmCreateLoadVisibilityWatcher();
  var _netAtStart = pmNetworkSnapshot();
  // The one place this load's state is mapped onto the signal object. Four
  // capture sites report it and they differ only in the status, so assembling
  // it per site is how they would drift apart on what they record.
  var _loadSignals = function(httpStatus) {
    return {
      hiddenAtStart: _loadVisibility.hiddenAtStart,
      hiddenDuring: _loadVisibility.hiddenDuring,
      hiddenAfterMs: _loadVisibility.hiddenAfterMs,
      pageHidden: document.visibilityState === 'hidden',
      pageUnloading: pmPageUnloading,
      httpStatus: httpStatus,
      netAtStart: _netAtStart
    };
  };
  var loadState = {
    id: ++pmThreadLoadSeq,
    threadId: String(thread_id),
    timeoutId: null,
    jqXHR: null,
    retried: false,
    firstFailureElapsedMs: null
  };
  activePmThreadLoad = loadState;
  window.pm_ajax_request_active = 1;

  // Latency hint: after 5s, reveal a "still loading" label under the spinner so
  // users see progress rather than a silent spinner. Do not abort the request.
  // The 30s AJAX timeout handles real hangs in the .fail() branch below.
  loadState.timeoutId = setTimeout(function() {
    if (!isActivePmThreadLoad(loadState)) return;
    // A backgrounded tab is not a user-perceived slow load, so don't count it.
    if (document.visibilityState === 'hidden') return;
    var stillEl = document.getElementById('pm-loading-still');
    if (stillEl) stillEl.classList.remove('hidden');
    // Sample the info-level slow-load report (the label above is always shown).
    if (Math.random() < PM_SLOW_LOAD_SAMPLE_RATE) {
      // Requests still running at 5s are the precursors of the 30s timeouts in
      // pm-load-failed, so they carry the same discriminators, and the two
      // groups cannot be joined after the fact: request_id restarts at 1 on
      // every page load, and this event is sampled.
      //
      // This is not a base rate for the hidden split. The guard above returns
      // before this point whenever the tab is hidden at 5s, so page_hidden is
      // always 0 here and hidden_during is 1 only for a tab that went hidden
      // and came back inside 5s. Comparing the hidden share of this group
      // against pm-load-failed measures that guard, not the population.
      // http_status is 'pending' rather than 0 because the request has not
      // failed, and tag queries span groups.
      capturePmThreadDiagnostic('pm-slow-load:5s', 'info', Object.assign({
        source: 'timeout',
        pm_slow_load_sampled: '1'
      }, pmDiagnosticSignalTags(_loadSignals('pending')), pmLayoutTags()), {
        thread_id: thread_id,
        request_id: loadState.id,
        active_thread_id: getActivePmThreadId(),
        elapsed_ms: Date.now() - _loadStart,
        hidden_after_ms: _loadVisibility.hiddenAfterMs
      });
    }
  }, 5000);

  var _issuePmThreadLoadRequest = function() {
    var _supersededByRetry = false;
    var _attemptIssuedAt = Date.now();

    return $.ajax({
    type: "POST",
    url: "/app/messages/load",
    data: {
      "thread": thread_id,
      // Skip server draft if we have local data (including explicit empty)
      "load_draft": (localDraft !== null || use_browser_cached_draft) ? 0 : 1
    },
    dataType: "json",
    timeout: 30000
  }).done(function(data, textStatus, jqXHR) {
    // Released here as well as in always(), because a throw anywhere below
    // skips every callback jQuery registered after this one.
    _loadVisibility.release();
    // Auth/CSRF/onboarding gate: the server returns {redirect: url} for an
    // expired session or stale token instead of a 302 to HTML. Let the global
    // ajaxComplete handler navigate, and skip rendering so the thread does not
    // flash an empty/error state on the way out.
    if (data && typeof data.redirect === 'string' && data.redirect) {
      clearPmThreadLoadTimeout(loadState);
      return;
    }
    if (!isActivePmThreadLoad(loadState)) {
      // A superseded request that still reached the server is the closest
      // thing to a control group: same discriminators, but the load demonstrably
      // was not torn down by the page going away.
      capturePmThreadDiagnostic('pm-load-stale-response', 'info', Object.assign({
        source: 'ajax',
        state: 'stale_response'
      }, pmDiagnosticSignalTags(_loadSignals(jqXHR.status))), {
        thread_id: thread_id,
        request_id: loadState.id,
        active_request_id: activePmThreadLoad ? activePmThreadLoad.id : null,
        active_thread_id: getActivePmThreadId(),
        elapsed_ms: Date.now() - _loadStart,
        hidden_after_ms: _loadVisibility.hiddenAfterMs
      });
      return;
    }
    // A successful second attempt is the observable outcome that separates a
    // transient connection reuse failure from repeatable request interception.
    if (loadState.retried) {
      if (Math.random() < PM_SLOW_LOAD_SAMPLE_RATE) {
        capturePmThreadDiagnostic('pm-load-recovered-on-retry', 'info', Object.assign({
          source: 'ajax',
          state: 'recovered'
        }, pmDiagnosticSignalTags(_loadSignals(jqXHR.status))), {
          thread_id: thread_id,
          request_id: loadState.id,
          active_thread_id: getActivePmThreadId(),
          elapsed_ms: Date.now() - _loadStart,
          first_failure_elapsed_ms: loadState.firstFailureElapsedMs,
          hidden_after_ms: _loadVisibility.hiddenAfterMs
        });
      }
    }
    clearPmThreadLoadTimeout(loadState);
    if (typeof gtag !== 'undefined') {
      gtag('event', 'thread_opened');
    }
    _keepScroll();
    // Opening a thread is also where the typing sender gets its token.
    window.storeThreadTypingToken?.(thread_id, data);
    if (data.body) {
      // Build new wrapper off-DOM, then swap in one operation to avoid blank flash
      var newWrapper = $('<div id="messages_wrapper"></div>');
      newWrapper.append(data.body);
      var oldWrapper = c.find('#messages_wrapper');
      if (oldWrapper.length) {
        oldWrapper.replaceWith(newWrapper);
      } else {
        c.empty();
        c.append(newWrapper);
      }
      // column-reverse: scrollTop=0 is bottom
      c.get(0).scrollTop = 0;
      _keepScroll();
      // Update "last opened by contact" timestamp
      if (typeof data.lobc != "undefined") {
        thread.data("lobc", data.lobc);
      }
      // Update header status from server response
      if (typeof data.contact_status != "undefined") {
        window.update_header_status(data.contact_status);
      }
      // Hide compose box and update header when the contact is inactive/deleted
      if (data.contact_active === false) {
        $("#threads_right_bottom").addClass("hidden");
        if (data.contact_name) {
          $("#current_thread_username").text(data.contact_name);
        }
      }
      // Load draft if any
      if (data.draft.length > 0) {
        msg.val(data.draft);
        window.rebaseMessageComposition?.(data.draft, 'restored_draft');
        $("#threads_main").addClass("send");
        msg[0].dispatchEvent(new Event('input'));
      }

      // Initialize audio players before scrolling so their height is accounted for
      if (window.initAudioPlayers) window.initAudioPlayers();

      // Setup scroll observer for dynamic content
      window.setupScrollObserver(c);

      // Wait for images to load, then scroll to bottom
      window.scrollToBottomAfterImagesLoad(c, 3000);

      // If user prefers paginated view, switch to it after loading
      if (localStorage.getItem('pm_paged_view') === '1') {
        window.activatePagedView();
      }
    } else {
      var errWrapper = $('<div id="messages_wrapper"></div>');
      if (data.error) {
        errWrapper.append(window.err_div.html(data.error));
      } else {
        errWrapper.append(window.err_div.html(window.t('common.pm.conversation_empty')));
      }
      var oldW = c.find('#messages_wrapper');
      if (oldW.length) { oldW.replaceWith(errWrapper); } else { c.empty(); c.append(errWrapper); }
      c.get(0).scrollTop = 0;
    }
    // Open user profile in side panel — backend gates view logging by thread state.
    if (window.SidePanel) {
      var uid = thread.data('uid');
      if (uid) {
        window.SidePanel.open(uid, { clickSource: 2, context: 'messages' });
      }
    }
    window.focus_msg_box();
  }).fail(function(jqXHR, textStatus, errorThrown) {
    if (!isActivePmThreadLoad(loadState)) {
      _loadVisibility.release();
      return;
    }
    if (textStatus === 'abort') {
      clearPmThreadLoadTimeout(loadState);
      _loadVisibility.release();
      return;
    }

    // A status-0 'error' teardown that coincides with the page unloading or being
    // backgrounded is a benign cancellation (navigating away, reload, tab
    // close, mobile app/tab backgrounding), not a server fault. pmPageUnloading
    // covers same-tab navigation/reload, where visibilityState can still read
    // 'visible' when the aborted request fails. Record it separately so it does
    // not inflate pm-load-failed, and skip the error UI since the page is gone
    // or hidden. A status-0 failure on a foreground, non-unloading page is a
    // genuine network failure (connection refused, DNS, TLS) and falls through
    // to pm-load-failed so the user sees an error instead of a stuck spinner.
    var _failSignals = _loadSignals(jqXHR.status);
    if (pmIsCancelledLoadTeardown(_failSignals, textStatus)) {
      clearPmThreadLoadTimeout(loadState);
      _loadVisibility.release();
      capturePmThreadDiagnostic('pm-load-cancelled', 'info', Object.assign({
        source: 'ajax',
        state: 'cancelled',
        text_status: textStatus
      }, pmDiagnosticSignalTags(_failSignals)), {
        textStatus: textStatus,
        thread_id: thread_id,
        request_id: loadState.id,
        active_thread_id: getActivePmThreadId(),
        elapsed_ms: Date.now() - _loadStart,
        hidden_after_ms: _loadVisibility.hiddenAfterMs
      });
      return;
    }

    var _elapsedMs = Date.now() - _loadStart;
    // Only a fast connection-level failure gets one immediate second attempt.
    // The shared load state prevents a retry from chaining or surviving a
    // superseding thread load.
    if (!loadState.retried && jqXHR.status === 0 && textStatus === 'error' && _elapsedMs < 2000) {
      // Marked before the re-issue: a transport that rejects synchronously runs
      // the second attempt's handlers before this call returns, and an unset
      // flag would let that rejection take this branch again.
      loadState.retried = true;
      loadState.firstFailureElapsedMs = _elapsedMs;
      // A throw on issue leaves no second attempt to finalize the load, and
      // jQuery runs these callbacks without try/catch, so it would escape past
      // always() and strand the spinner. Fall through to the failure path.
      try {
        loadState.jqXHR = _issuePmThreadLoadRequest();
        _supersededByRetry = true;
        return;
      } catch (e) {
        loadState.retried = false;
        loadState.firstFailureElapsedMs = null;
      }
    }

    clearPmThreadLoadTimeout(loadState);
    _loadVisibility.release();

    var failureState = textStatus === 'parsererror' ? 'parse_failure' : 'request_failure';
    var _perfEntry = pmResourceTimingEntryForRequest(PM_THREAD_LOAD_URL, _attemptIssuedAt);
    console.warn('[pm-diag] load failed: ' + textStatus + ' status=' + jqXHR.status);
    // page_hidden / page_unloading are recorded here as well as on the
    // cancellation branch above, and reaching this line does not mean both read
    // false. Only the status-0 'error'/'' shape is diverted there, so a timeout,
    // a non-zero HTTP status, or a parsererror on a hidden or unloading page
    // lands here with these fields legitimately true.
    //
    // The group therefore holds at least four shapes: a foreground status-0
    // 'error', a timeout at either visibility, any non-zero HTTP status, and a
    // parsererror on 200. Splitting it needs textStatus and httpStatus together
    // with the visibility fields, never the visibility fields alone.
    //
    // The network fields carry the state at the moment of failure, not at the
    // moment the request began, so they describe request-time conditions only
    // when elapsed_ms is small. That covers the dominant shape here, a status-0
    // 'error' that fails at the connection layer within a second, where a
    // dropped or switching mobile connection explains the record as readily as
    // a server-side fault. Over a 30s timeout they are the endpoint reading and
    // nothing more. connection.effectiveType is absent on Safari and reports
    // 'unknown', so the field is only meaningful read against browser.name.
    capturePmThreadDiagnostic('pm-load-failed', 'warning', Object.assign({
      source: 'ajax',
      state: failureState,
      text_status: textStatus,
      retried: loadState.retried ? '1' : '0',
      perf_entry_present: _perfEntry ? '1' : '0'
    }, pmDiagnosticSignalTags(_failSignals), pmLayoutTags()), {
      textStatus: textStatus,
      errorThrown: errorThrown,
      thread_id: thread_id,
      request_id: loadState.id,
      active_thread_id: getActivePmThreadId(),
      elapsed_ms: Date.now() - _loadStart,
      hidden_after_ms: _loadVisibility.hiddenAfterMs,
      // Same-origin timing exposes connect values. Restricted cross-origin entries use zeros.
      perf_connect_duration_ms: _perfEntry ? Math.round(_perfEntry.connectEnd - _perfEntry.connectStart) : null
    });

    // Carries the same discriminators as the pm-load-failed above rather than
    // relying on a join back to it. request_id restarts at 1 on every page
    // load, so the two events cannot be matched up after the fact. The network
    // fields earn their place here specifically: a captive portal answering
    // with its own HTML login page is a 200 that fails to parse, and it is the
    // network reading rather than content_type that tells that apart from a
    // backend returning the wrong content type.
    if (failureState === 'parse_failure') {
      capturePmThreadDiagnostic('pm-load-parse-failed', 'warning', Object.assign({
        source: 'ajax',
        state: 'parse_failure',
        retried: loadState.retried ? '1' : '0',
        perf_entry_present: _perfEntry ? '1' : '0'
      }, pmDiagnosticSignalTags(_failSignals), pmLayoutTags()), {
        thread_id: thread_id,
        request_id: loadState.id,
        active_thread_id: getActivePmThreadId(),
        response_length: jqXHR.responseText ? jqXHR.responseText.length : 0,
        content_type: jqXHR.getResponseHeader ? jqXHR.getResponseHeader('content-type') : null,
        elapsed_ms: Date.now() - _loadStart
      });
    }

    var errWrapper = $('<div id="messages_wrapper"></div>');
    errWrapper.append(window.err_div.html(window.err_txt));
    var oldW = c.find('#messages_wrapper');
    if (oldW.length) { oldW.replaceWith(errWrapper); } else { c.empty(); c.append(errWrapper); }
  }).always(function() {
    // The retry's callback chain owns shared load finalization. Finalizing from
    // the failed first attempt would clear window.pm_ajax_request_active, which
    // polling.js reads to hold a poll off while a load runs, and would drop the
    // in-flight retry out of reach of cancelActivePmThreadLoad.
    if (_supersededByRetry) return;
    _loadVisibility.release();
    finishPmThreadLoad(loadState);
  });
  };

  loadState.jqXHR = _issuePmThreadLoadRequest();
  return false;
};

window.close_thread = function(no_history) {
  // Hide the Message Requests action bar when leaving a conversation (#3039).
  var _reqBar = document.getElementById('message-request-bar');
  if (_reqBar) _reqBar.classList.add('hidden');

  var canceledLoad = cancelActivePmThreadLoad();
  if (canceledLoad) {
    finishPmThreadLoad(canceledLoad);
  }

  // Close side panel profile view
  if (window.SidePanel) window.SidePanel.close();

  // If html5 history not supported use redirect
  if (!window.htm5_history_supported) {
    window.location.href = window.location.protocol + "//" +
      window.location.hostname + window.location.pathname;
    return false;
  }

  // Dismiss the virtual keyboard so visualViewport reports the full height
  if (document.activeElement && document.activeElement !== document.body) {
    document.activeElement.blur();
  }

  // Clear keyboard-open state and inline styles from mobile keyboard handling
  var pmContainer = document.querySelector('.privateMessages');
  if (pmContainer) {
    pmContainer.classList.remove('keyboard-open');
    pmContainer.style.removeProperty('height');
    pmContainer.style.removeProperty('top');
  }
  var bottomNav = document.querySelector('[data-component="mobile-bottom-nav"]');
  if (bottomNav) bottomNav.style.display = '';

  window.update_change_view_link();

  let tm = $("#threads_main");
  $("#close_thread_wrap").hide();
  tm.find(".pm_thread").removeClass("active");

  // Hide the conversation header and message compose box
  $("#conversation-header").addClass("hidden");
  $("#threads_right_bottom").addClass("hidden");
  document.dispatchEvent(new CustomEvent("pm:thread-closed"));

  // Hide jump-to-latest and pagination controls
  $("#jump-to-latest-btn").addClass("hidden").removeClass("flex");
  $("#paged-view-controls").addClass("hidden");
  window.pagedViewActive = false;

  // Reset date-jump state and calendar
  if (window.calendarJump) {
    window.calendarJump.close();
    window.calendarJump.dateJumpActive = false;
  }
  window.calendarJump._jumpState = null;
  var trEl = document.getElementById('threads_right');
  if (trEl) trEl.style.flexDirection = 'column-reverse';

  // Clear the send thread ID and message input
  $("#send_thread_id").val("");
  $("#message").val("");
  window.rebaseMessageComposition?.('', 'empty');

  // Clear photo upload
  if (window.photoUpload) {
    window.photoUpload.removeAllPhotos();
  }

  // Hide any open dropdown menus
  $("#conversation-actions-menu").addClass("hidden");
  if (window.SwipeActions) window.SwipeActions.closeAll();

  // Clear messages and restore the empty-state placeholder
  var $tr = $("#threads_right");
  $tr.empty();
  var title = $tr.data('empty-title') || '';
  var subtitle = $tr.data('empty-subtitle') || '';
  $tr.append(
    '<div id="pm-empty-state" class="flex items-center justify-center h-full text-center flex-1">' +
      '<div class="flex flex-col items-center justify-center max-w-sm px-4">' +
        '<div class="relative w-44 h-36 sm:w-52 sm:h-40 mb-6">' +
          '<img src="/images/favicon.svg" alt="" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 sm:w-24 sm:h-24 drop-shadow-lg" />' +
          '<div class="absolute w-10 h-10 sm:w-12 sm:h-12 top-0 left-4 -rotate-12 bg-blue-100 rounded-xl flex items-center justify-center drop-shadow-md"><i data-lucide="message-circle" class="w-5 h-5 sm:w-6 sm:h-6 text-blue-500"></i></div>' +
          '<div class="absolute w-9 h-9 sm:w-11 sm:h-11 top-1 right-4 rotate-12 bg-emerald-100 rounded-xl flex items-center justify-center drop-shadow-md"><i data-lucide="smile" class="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500"></i></div>' +
          '<div class="absolute w-10 h-10 sm:w-12 sm:h-12 bottom-0 left-6 rotate-6 bg-amber-100 rounded-xl flex items-center justify-center drop-shadow-md"><i data-lucide="globe" class="w-5 h-5 sm:w-6 sm:h-6 text-amber-500"></i></div>' +
          '<div class="absolute w-9 h-9 sm:w-11 sm:h-11 bottom-1 right-6 -rotate-6 bg-purple-100 rounded-xl flex items-center justify-center drop-shadow-md"><i data-lucide="heart" class="w-5 h-5 sm:w-6 sm:h-6 text-purple-500"></i></div>' +
        '</div>' +
        '<h3 class="text-xl sm:text-2xl font-bold text-gray-800 mb-2">' + $('<span>').text(title).html() + '</h3>' +
        '<p class="text-gray-500 text-sm sm:text-base">' + $('<span>').text(subtitle).html() + '</p>' +
      '</div>' +
    '</div>'
  );
  if (window.LucideIcons) window.LucideIcons.initialize($tr[0]);

  // Show the right container but keep it in empty state
  $("#threads-right__container").removeClass("hidden").addClass("flex");
  $("#pm-dismiss-placeholder-btn").removeClass("hidden");

  tm.removeClass("threads--open");

  // Update responsive layout, then apply placeholder preference
  window.threads_main_resize();
  window.applyPlaceholderPreference();

  if (typeof no_history != "undefined" && no_history) {
    return false;
  }

  // Push url to html5 history
  let stateObj = { "close_thread": true };
  history.pushState(stateObj, null, "/app/messages");
};
